import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExamItemDetailViewModel, ExamItemEditorViewModel } from '@/shared/api/generated';
import {
  getApiV1ExamQuestionLibraryItemExamitemidQueryKey,
  useGetApiV1ExamQuestionLibraryItemExamitemid,
  usePutApiV1ExamQuestionLibraryItemId,
} from '@/shared/api/generated';

import { useSession } from '@/shared/store/hooks/useSession';

import type {
  Question,
  QuestionLanguage,
  QuestionStatus,
  QuestionType,
} from './useQuestionLibrary';
import { STATUS_MAP } from './useQuestionLibrary';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Alternativa de uma questão de múltipla escolha ou verdadeiro/falso */
export interface QuestionAlternative {
  readonly id: string;
  /** ID original da API — separado do id de UI para evitar duplicatas */
  readonly originalId?: number;
  readonly value: string;
  readonly isCorrect: boolean;
}

/** Modelo completo da questão para o formulário de edição */
export interface QuestionDetail extends Question {
  readonly weight: number;
  /** Nível de dificuldade numérico retornado pelo endpoint /api/v1/exam/exam-item-levels */
  readonly level: number | null;
  readonly showLabel: boolean;
  readonly showInformation: boolean;
  readonly teacherEvaluation: boolean;
  readonly randomizeAlternatives: boolean;
  readonly requiredField: boolean;
  readonly alwaysInEvaluation: boolean;
  readonly pageBreak: boolean;
  readonly information: string;
  readonly item: string;
  readonly alternatives: QuestionAlternative[];
  /** Usuário que realizou a última atualização. Null quando não retornado pela API de preview. */
  readonly updatedBy: string | null;
  /** Data da última atualização. Null quando não retornada pela API de preview. */
  readonly updatedAt: string | null;
  /** ID numérico do autor (personId). Null quando não disponível via preview. */
  readonly authorId: number | null;
  /** ID numérico da disciplina. Null quando não disponível via preview. */
  readonly disciplineId: number | null;
  /** ID numérico da empresa. Mapeado de `companyId`. */
  readonly companyId: number | null;
  /** ID numérico do tipo de componente da questão (1=SingleChoice, 2=MultipleChoice, etc). */
  readonly componentType: number;
  /** Valor mínimo (Numeric/Grade). */
  readonly min: number | null;
  /** Valor máximo (Numeric/Grade). */
  readonly max: number | null;
  /** Resposta correta como string (Numeric). */
  readonly correctAnswer: string | null;
  /** Casas decimais (Numeric). */
  readonly decimalPlaces: number | null;
  /** Tempo máximo de áudio em segundos (Audio). */
  readonly maxAudioTimeInSeconds: number | null;
  /** Tamanho máximo de arquivo em MB (Attachment). */
  readonly maxFileSizeInMegabytes: number | null;
  /** Formatos aceitos para upload (Attachment). */
  readonly acceptedFormats: readonly string[];
  /**
   * Subtipo dissertativo (MultilineText). Ver `EExamItemSubtype`.
   * Null quando o tipo não aceita subtipo ou nenhum foi cadastrado.
   */
  readonly subtype: number | null;
  /**
   * Resposta-modelo (subtipos `ModelAnswerEssay` e `MbaCase`) ou Notas de Ensino
   * (`MbaCase`), usada como referência na correção por IA. Máx. 1000 caracteres.
   */
  readonly modelAnswer: string | null;
  /**
   * Palavras-chave esperadas na resposta, separadas por vírgula. Apenas para o
   * subtipo `ModelAnswerEssay`. Máx. 1000 caracteres.
   */
  readonly keyTerms: string | null;
  /**
   * Status de aprovação numérico (`EExamItemStatus`: 1=Pendente, 2=Aprovado,
   * 3=Rejeitado). Guardado separado do `status` de domínio (string) porque é o
   * valor que o PUT espera de volta.
   */
  readonly statusId: number | null;
  /** Justificativa do status de aprovação. */
  readonly statusJustification: string | null;
  /** Indica que o usuário aceitou uma sugestão de revisão com IA nesta edição. */
  readonly isAiAssisted?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const COMPONENT_TYPE_ID_TO_NAME: Record<number, string> = {
  1: 'SingleChoice',
  2: 'MultipleChoice',
  3: 'SingleLineText',
  4: 'MultilineText',
  5: 'Numeric',
  6: 'Grade',
  7: 'Audio',
  8: 'Attachment',
};

function mapDetailToQuestion(detail: ExamItemDetailViewModel): QuestionDetail {
  const alternatives: QuestionAlternative[] = (detail.examTypeListValues ?? []).map((t, index) => ({
    id: String(index),
    ...(t.id != null ? { originalId: t.id } : {}),
    value: t.value ?? '',
    isCorrect: t.correctAnswer ?? false,
  }));

  return {
    id: String(detail.id ?? ''),
    text: detail.label ?? '',
    discipline: '',
    author: '',
    language: (detail.cultureInfo ?? 'pt-BR') as QuestionLanguage,
    status: STATUS_MAP[detail.status ?? 0] ?? ('Pendente' as QuestionStatus),
    isActive: detail.active ?? false,
    type: (COMPONENT_TYPE_ID_TO_NAME[detail.componentType ?? 0] ?? '') as QuestionType,
    isOwner: false,
    company: detail.companyId != null ? String(detail.companyId) : null,
    createdAt: detail.creationDate ?? null,
    weight: detail.weight ?? 1,
    level: detail.level ?? null,
    showLabel: detail.showLabel ?? false,
    showInformation: detail.showInformation ?? false,
    teacherEvaluation: detail.teacherEvaluation ?? false,
    randomizeAlternatives: detail.randomizeAlternatives ?? false,
    requiredField: detail.required ?? false,
    alwaysInEvaluation: detail.fixed ?? false,
    pageBreak: detail.pageBreak ?? false,
    information: detail.information ?? '',
    item: '',
    alternatives,
    updatedBy: detail.userLastChange ?? null,
    updatedAt: detail.lastChangeDate ?? null,
    authorId: detail.personAuthorId ?? null,
    disciplineId: detail.disciplineId ?? null,
    companyId: detail.companyId ?? null,
    componentType: detail.componentType ?? 1,
    min: detail.min ?? null,
    max: detail.max ?? null,
    correctAnswer: detail.correctAnswer ?? null,
    decimalPlaces: detail.decimalPlaces ?? null,
    maxAudioTimeInSeconds: detail.maxAudioTimeInSeconds ?? null,
    maxFileSizeInMegabytes: detail.maxFileSizeInMegaBytes ?? null,
    acceptedFormats: Array.isArray(detail.acceptedFormats) ? detail.acceptedFormats : [],
    subtype: detail.subtype ?? null,
    modelAnswer: detail.modelAnswer ?? null,
    keyTerms: detail.keyTerms ?? null,
    statusId: detail.status ?? null,
    statusJustification: detail.statusJustification ?? null,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Hook para edição de uma questão da Biblioteca de Questões.
 *
 * Carrega os detalhes da questão via `useGetApiV1ExamQuestionLibraryItemExamitemid`
 * e salva via `usePutApiV1ExamQuestionLibraryItemId`.
 *
 * @param questionId - ID da questão a editar (string)
 * @returns Detalhes da questão, estado de loading/saving e função de save
 *
 * @example
 * ```tsx
 * const { question, isLoading, save, isSaving } = useQuestionEdit('42');
 * ```
 */
export function useQuestionEdit(questionId: string) {
  const queryClient = useQueryClient();
  const { session } = useSession();
  const { t } = useTranslation('questionLibrary');
  const { alert } = useAlert();
  const numericId = Number(questionId);

  const query = useGetApiV1ExamQuestionLibraryItemExamitemid(numericId, {
    query: { enabled: !Number.isNaN(numericId) && numericId > 0 },
  });

  const mutation = usePutApiV1ExamQuestionLibraryItemId({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamQuestionLibraryItemExamitemidQueryKey(numericId),
        });
        alert({
          alertType: 'success',
          title: t('alerts.editSuccess.title'),
          description: t('alerts.editSuccess.description'),
        });
      },
      onError: () => {
        alert({
          alertType: 'danger',
          title: t('alerts.editError.title'),
          description: t('alerts.editError.description'),
        });
      },
    },
  });

  const question: QuestionDetail | null = query.data ? mapDetailToQuestion(query.data) : null;

  const save = useCallback(
    (data: Partial<QuestionDetail>) => {
      if (!query.data?.id || !session?.personId) return;

      const payload: ExamItemEditorViewModel = {
        id: query.data.id,
        componentType: data.componentType ?? query.data.componentType ?? 1,
        cultureInfo: data.language ?? query.data.cultureInfo ?? 'pt-BR',
        label: data.text ?? query.data.label ?? '',
        personAuthorId: data.authorId ?? session.personId,
        information: data.information ?? query.data.information ?? null,
        showLabel: data.showLabel ?? query.data.showLabel ?? false,
        showInformation: data.showInformation ?? query.data.showInformation ?? false,
        pageBreak: data.pageBreak ?? query.data.pageBreak ?? false,
        ...(data.randomizeAlternatives !== undefined
          ? { randomizeAlternatives: data.randomizeAlternatives }
          : {}),
        teacherEvaluation: data.teacherEvaluation ?? null,
        ...(data.isActive !== undefined
          ? { active: data.isActive }
          : query.data.active !== undefined
            ? { active: query.data.active }
            : {}),
        weight: data.weight ?? 1,
        ...(data.level != null ? { level: data.level } : {}),
        ...(data.requiredField !== undefined ? { required: data.requiredField } : {}),
        ...(data.alwaysInEvaluation !== undefined ? { fixed: data.alwaysInEvaluation } : {}),
        ...(data.alternatives && data.alternatives.length > 0
          ? {
              examTypeList: {
                examTypeListValues: data.alternatives.map(alt => ({
                  ...(alt.originalId ? { id: alt.originalId } : {}),
                  value: alt.value,
                  correctAnswer: alt.isCorrect,
                })),
              },
            }
          : {}),
        ...(data.disciplineId != null ? { disciplineId: data.disciplineId } : {}),
        ...(data.companyId != null
          ? { companyId: data.companyId }
          : query.data.companyId != null
            ? { companyId: query.data.companyId }
            : {}),
        ...(data.min != null ? { min: data.min } : {}),
        ...(data.max != null ? { max: data.max } : {}),
        ...(data.correctAnswer != null ? { correctAnswer: data.correctAnswer } : {}),
        ...(data.decimalPlaces != null ? { decimalPlaces: data.decimalPlaces } : {}),
        ...(data.maxAudioTimeInSeconds != null
          ? { maxAudioTimeInSeconds: data.maxAudioTimeInSeconds }
          : {}),
        ...(data.maxFileSizeInMegabytes != null
          ? { maxFileSizeInMegabytes: data.maxFileSizeInMegabytes }
          : {}),
        ...(data.acceptedFormats && data.acceptedFormats.length > 0
          ? { acceptedFormats: [...data.acceptedFormats] }
          : {}),
        // Status de aprovação: o formulário não o edita, mas o PUT aceita o campo
        // e o GET o devolve — reenviamos o valor recebido para que salvar uma
        // questão já aprovada não a jogue de volta para pendente no backend.
        ...(data.statusId != null
          ? { status: data.statusId }
          : query.data.status != null
            ? { status: query.data.status }
            : {}),
        ...(data.statusJustification !== undefined
          ? { statusJustification: data.statusJustification }
          : query.data.statusJustification !== undefined
            ? { statusJustification: query.data.statusJustification }
            : {}),
        // Subtipo dissertativo: enviados explicitamente (inclusive `null`) para
        // limpar valores antigos quando o tipo/subtipo deixa de aceitá-los.
        ...(data.subtype !== undefined ? { subtype: data.subtype } : {}),
        ...(data.modelAnswer !== undefined ? { modelAnswer: data.modelAnswer } : {}),
        ...(data.keyTerms !== undefined ? { keyTerms: data.keyTerms } : {}),
        ...(data.isAiAssisted ? { isAiAssisted: true } : {}),
      };

      mutation.mutate({ id: query.data.id, data: payload });
    },
    [query.data, mutation, session],
  );

  return {
    question,
    isLoading: query.isPending,
    isError: query.isError,
    save,
    isSaving: mutation.isPending,
  } as const;
}
